/**
 * Seg — spec §F2 `Seg(options, value, onChange)`, the mockup's `.seg` segmented control.
 */
export interface SegOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  title?: string;
  /**
   * Optional `data-testid` for this option's own button. The control is a `radiogroup` of
   * unlabelled-by-id buttons, so an option a test or an e2e spec has to name (§F8's
   * `cat-view-grid`/`cat-view-list`) carries its hook here rather than being selected by
   * its translated label.
   */
  testId?: string;
}

export interface SegProps<T extends string> {
  options: SegOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  /**
   * Skin class. Defaults to the mockup's `.seg`; the §F5.6 legend uses the mockup's
   * tighter `.lg-mode` variant of the same control.
   */
  className?: string;
}

export function Seg<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className = 'seg',
}: SegProps<T>) {
  return (
    <div className={className} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={o.value === value ? 'active' : ''}
          disabled={o.disabled}
          title={o.title}
          data-testid={o.testId}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
